import type { SeniorityConfig } from '../entities/RuntimeConfig';
import { CalendarDate } from '../value-objects/CalendarDate';

/**
 * How many vacation days a person is entitled to.
 *
 * Mexican law (LFT art. 76, as reformed in 2023) grants days by completed years
 * of service on a step table: 12 days after the first year, rising to 20 by the
 * fifth, then in two-day steps every five years. The table is runtime
 * configuration because companies grant more than the minimum and because the
 * law changes — the 2023 reform doubled the starting entitlement, and a
 * hard-coded table would have needed a deploy on the day it took effect.
 *
 * The statutory minimum is applied as a floor regardless of the table, so a
 * misconfigured bracket cannot drop someone below what the law requires.
 */

export interface EntitlementInput {
  readonly hireDate: CalendarDate | null;
  readonly asOf: CalendarDate;
  readonly seniority: SeniorityConfig;
  /** The floor from `RulesConfig.legalMinimumDays`. */
  readonly legalMinimumDays: number;
}

export interface Entitlement {
  readonly days: number;
  /** Completed years of service at `asOf`. */
  readonly completedYears: number;
  /** The bracket that applied, or null when the table was off or unmatched. */
  readonly appliedBracket: { readonly fromYear: number; readonly days: number } | null;
  /** True when the legal floor raised the figure above what the table gave. */
  readonly floorApplied: boolean;
}

/**
 * Completed years of service — anniversaries reached, not elapsed time rounded.
 * Someone hired on 1 March 2025 has completed zero years on 28 February 2026 and
 * one year on 1 March. Getting this wrong by a day grants a year's entitlement
 * early.
 */
export const completedYearsOfService = (
  hireDate: CalendarDate,
  asOf: CalendarDate,
): number => {
  if (asOf.isBefore(hireDate)) return 0;

  let years = asOf.year - hireDate.year;

  // Not yet at this year's anniversary.
  const anniversaryThisYear = hireDate.addYears(years);
  if (anniversaryThisYear.isAfter(asOf)) years -= 1;

  return Math.max(0, years);
};

/**
 * The date a person's right to vacation vests: one year of service, to the day.
 *
 * Anniversary to anniversary, never January to December. Someone hired on 23
 * March 2026 vests on 23 March 2027 — not on 1 January 2027, and not after
 * "twelve months of calendar year".
 */
export const vacationVestsOn = (hireDate: CalendarDate): CalendarDate => hireDate.addYears(1);

export interface VacationEligibility {
  /** Whether the right has vested — whether they may *take* days today. */
  readonly eligible: boolean;
  /** The day the right vests. Null when there is no hire date on record. */
  readonly vestsOn: CalendarDate | null;
  /** Calendar days still to wait. Zero once eligible. */
  readonly daysRemaining: number;
  /**
   * The first day they may book.
   *
   * Not vesting alone: somebody who has not completed their year may still plan
   * ahead for dates *after* it, which is the useful behaviour — refusing to let
   * a new hire open the wizard at all would make them wait a year to schedule a
   * trip they can legitimately take on day 366. Before vesting this is the
   * anniversary; after it, today.
   */
  readonly earliestBookable: CalendarDate;
}

/**
 * When someone's vacation becomes bookable.
 *
 * Entitlement and eligibility are different questions and this is the second
 * one. `resolveEntitlement` never returns less than the legal minimum, on
 * purpose — an incomplete record must not read as "no vacation" — so a person
 * hired last month already shows twelve days. Those days are real; what they are
 * not is *takeable yet*, because under the LFT the right vests on completing the
 * first year. Without this the portal would let a new hire book them the week
 * they arrived.
 *
 * What it does **not** do is stop them planning. The rule constrains the dates
 * they may choose, not their access to the wizard: a person hired in March may
 * schedule the following April the day they join.
 *
 * **A missing hire date does not block.** It is the same call
 * `resolveEntitlement` makes and for the same reason: every record in a fresh
 * install has a null hire date, so blocking on absence would lock out the entire
 * company rather than the handful of people it is meant to hold back. HR filling
 * the date in is what turns the rule on for that person.
 */
export const vacationEligibility = (input: {
  readonly hireDate: CalendarDate | null;
  readonly asOf: CalendarDate;
}): VacationEligibility => {
  const { hireDate, asOf } = input;

  /*
   * Mañana, no hoy.
   *
   * `submitVacationRequest` rechaza toda solicitud cuyo inicio sea hoy o antes
   * —«esas fechas ya empezaron; las vacaciones se solicitan por adelantado»— y
   * esto devolvía hoy. El calendario ofrecía un día que el servidor no acepta, y
   * los atajos «un día», «una semana» y «dos semanas», que arrancan de aquí,
   * empezaban todos en un día imposible. QA lo reportó tres veces, como tres
   * defectos distintos: eran éste.
   *
   * Un día que no se puede reservar no es «el primero reservable», así que la
   * corrección va aquí y no en cada sitio que lo usa.
   */
  const manana = asOf.addDays(1);

  if (hireDate === null) {
    return { eligible: true, vestsOn: null, daysRemaining: 0, earliestBookable: manana };
  }

  const vestsOn = vacationVestsOn(hireDate);
  const eligible = !vestsOn.isAfter(asOf);

  return {
    eligible,
    vestsOn,
    daysRemaining: eligible ? 0 : asOf.daysUntil(vestsOn),
    /* Quien cumple el año hoy tampoco puede reservar hoy: el freno del servidor
       es sobre la fecha pedida, no sobre la antigüedad. */
    earliestBookable: eligible ? manana : CalendarDate.max(vestsOn, manana),
  };
};

/**
 * Resolves entitlement.
 *
 * With the table off, everyone gets `fixedDays`. With it on, the applicable
 * bracket is the highest whose `fromYear` the tenure has reached — eight years of
 * service matches the `fromYear: 6` bracket, because the table's steps are
 * open-ended ranges, not exact matches.
 *
 * A person with no hire date on record gets the legal minimum rather than zero:
 * an incomplete HR record must not read as "this person has no vacation".
 *
 * Note this answers "how many days", not "may they be taken yet" — see
 * `vacationEligibility` for the first-year rule.
 */
export const resolveEntitlement = (input: EntitlementInput): Entitlement => {
  const { hireDate, asOf, seniority, legalMinimumDays } = input;

  if (hireDate === null) {
    return {
      days: legalMinimumDays,
      completedYears: 0,
      appliedBracket: null,
      floorApplied: true,
    };
  }

  const completedYears = completedYearsOfService(hireDate, asOf);

  if (!seniority.enabled) {
    const days = Math.max(seniority.fixedDays, legalMinimumDays);
    return {
      days,
      completedYears,
      appliedBracket: null,
      floorApplied: days > seniority.fixedDays,
    };
  }

  // Highest bracket the tenure reaches. Sorting defensively: the stored table is
  // user-edited and may arrive out of order.
  const applicable = [...seniority.brackets]
    .sort((a, b) => a.fromYear - b.fromYear)
    .filter((bracket) => completedYears >= bracket.fromYear);

  const appliedBracket = applicable.at(-1) ?? null;
  // The bracket is the law's floor; whatever the company adds sits on top of it
  // rather than inside the table, so the statutory figure stays readable.
  const tableDays = (appliedBracket?.days ?? 0) + seniority.additionalDays;
  const days = Math.max(tableDays, legalMinimumDays);

  return {
    days,
    completedYears,
    appliedBracket,
    floorApplied: days > tableDays,
  };
};

export type BracketEditError =
  | { readonly kind: 'duplicate-year'; readonly fromYear: number }
  | { readonly kind: 'invalid-year'; readonly fromYear: number }
  | { readonly kind: 'invalid-days'; readonly days: number };

/** Upper bounds on an edited bracket. Guards against a typo, not policy. */
const MAX_YEARS = 60;
const MAX_DAYS = 365;

/**
 * Adds a bracket to the table, keeping it sorted and rejecting a duplicate year.
 * Two brackets starting the same year would make entitlement depend on array
 * order, which is exactly the kind of silent ambiguity a payroll figure must not
 * have.
 */
export const addBracket = (
  seniority: SeniorityConfig,
  fromYear: number,
  days: number,
):
  | { readonly ok: true; readonly seniority: SeniorityConfig }
  | { readonly ok: false; readonly error: BracketEditError } => {
  if (!Number.isInteger(fromYear) || fromYear < 1 || fromYear > MAX_YEARS) {
    return { ok: false, error: { kind: 'invalid-year', fromYear } };
  }
  if (!Number.isInteger(days) || days < 0 || days > MAX_DAYS) {
    return { ok: false, error: { kind: 'invalid-days', days } };
  }
  if (seniority.brackets.some((b) => b.fromYear === fromYear)) {
    return { ok: false, error: { kind: 'duplicate-year', fromYear } };
  }

  const brackets = [...seniority.brackets, { fromYear, days }].sort(
    (a, b) => a.fromYear - b.fromYear,
  );

  return { ok: true, seniority: { ...seniority, brackets } };
};

export const removeBracket = (
  seniority: SeniorityConfig,
  fromYear: number,
): SeniorityConfig => ({
  ...seniority,
  brackets: seniority.brackets.filter((b) => b.fromYear !== fromYear),
});

/**
 * The table as rows for display, each showing the span it covers. The last
 * bracket is open-ended ("16 años o más"), which the design shows explicitly so
 * nobody reads it as applying to exactly 16 years.
 */
export interface BracketRow {
  readonly fromYear: number;
  readonly toYear: number | null;
  readonly days: number;
  readonly label: string;
}

export const bracketRows = (seniority: SeniorityConfig): readonly BracketRow[] => {
  const sorted = [...seniority.brackets].sort((a, b) => a.fromYear - b.fromYear);

  return sorted.map((bracket, index) => {
    const next = sorted[index + 1];
    const toYear = next === undefined ? null : next.fromYear - 1;
    const label =
      toYear === null
        ? `${bracket.fromYear} años o más`
        : toYear === bracket.fromYear
          ? `${bracket.fromYear} ${bracket.fromYear === 1 ? 'año' : 'años'}`
          : `${bracket.fromYear} – ${toYear} años`;

    return { fromYear: bracket.fromYear, toYear, days: bracket.days, label };
  });
};
